import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { FileText, Printer, Download, Receipt, Copy, Send } from "lucide-react";
import Layout from "../components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN'
  }).format(amount);
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-MX', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
};

const numeroALetras = (num) => {
  const unidades = ['', 'UN', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
  const decenas = ['', '', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
  const especiales = ['DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISÉIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE'];
  const centenas = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];

  if (num === 0) return 'CERO';
  if (num === 100) return 'CIEN';

  let resultado = '';

  // Miles
  if (num >= 1000) {
    const miles = Math.floor(num / 1000);
    if (miles === 1) {
      resultado += 'MIL ';
    } else {
      resultado += numeroALetras(miles) + ' MIL ';
    }
    num %= 1000;
  }

  // Centenas
  if (num >= 100) {
    const cent = Math.floor(num / 100);
    resultado += centenas[cent] + ' ';
    num %= 100;
  }

  // Decenas y unidades
  if (num >= 20) {
    const dec = Math.floor(num / 10);
    resultado += decenas[dec];
    num %= 10;
    if (num > 0) {
      resultado += ' Y ' + unidades[num];
    }
  } else if (num >= 10) {
    resultado += especiales[num - 10];
  } else if (num > 0) {
    resultado += unidades[num];
  }

  return resultado.trim();
};

export default function Documentos({ user, onLogout }) {
  const navigate = useNavigate();
  const [contratos, setContratos] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [selectedContrato, setSelectedContrato] = useState("");
  const [selectedCargo, setSelectedCargo] = useState("");
  const [selectedPago, setSelectedPago] = useState("");
  const [tipoRecibo, setTipoRecibo] = useState("doble"); // "doble" o "sencillo"
  const [loading, setLoading] = useState(false);
  const [logoBase64, setLogoBase64] = useState(null);

  // Load logo on mount
  useEffect(() => {
    const loadLogo = async () => {
      try {
        const response = await fetch('/logo.png');
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          setLogoBase64(reader.result);
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error('Error loading logo:', error);
      }
    };
    loadLogo();
  }, []);

  useEffect(() => {
    fetchContratos();
  }, []);

  useEffect(() => {
    if (selectedContrato) {
      fetchCargos(selectedContrato);
    }
  }, [selectedContrato]);

  useEffect(() => {
    if (selectedCargo) {
      fetchPagos(selectedCargo);
    }
  }, [selectedCargo]);

  const fetchContratos = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/contracts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContratos(response.data);
    } catch (error) {
      toast.error("Error al cargar contratos");
      console.error(error);
    }
  };

  const fetchCargos = async (contratoId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/cobranza/cargos/contrato/${contratoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Show all cargos (not just paid ones) - receipts can be generated before payment
      setCargos(response.data);
      setSelectedCargo("");
      setPagos([]);
      setSelectedPago("");
    } catch (error) {
      toast.error("Error al cargar cargos");
      console.error(error);
    }
  };

  const fetchPagos = async (cargoId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/cobranza/pagos/cargo/${cargoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPagos(response.data);
      setSelectedPago("");
    } catch (error) {
      toast.error("Error al cargar pagos");
      console.error(error);
    }
  };

  const generarRecibo = () => {
    if (!selectedCargo) {
      toast.error("Por favor selecciona un cargo");
      return;
    }

    setLoading(true);

    try {
      const pago = selectedPago ? pagos.find(p => p.id === selectedPago) : null;
      const cargo = cargos.find(c => c.id === selectedCargo);
      const contrato = contratos.find(c => c.id === selectedContrato);

      if (!cargo || !contrato) {
        toast.error("Error al obtener los datos");
        setLoading(false);
        return;
      }

      // Use payment data if selected, otherwise use charge data with current date
      const fechaPago = pago ? pago.fecha_pago : new Date().toISOString().split('T')[0];
      const importePago = pago ? pago.importe : cargo.saldo || cargo.importe;
      const metodoPago = pago ? (pago.metodo_pago || 'Efectivo') : 'Por definir';
      const referenciaPago = pago ? pago.referencia : null;
      const reciboId = pago ? pago.id.substring(0, 8).toUpperCase() : `RC-${Date.now().toString().substring(5, 13)}`;

      const doc = new jsPDF();
      const pageHeight = doc.internal.pageSize.height;
      const pageWidth = doc.internal.pageSize.width;
      
      // Determinar cuántos recibos generar y el offset
      const numRecibos = tipoRecibo === "doble" ? 2 : 1;
      const baseYOffset = tipoRecibo === "sencillo" ? 30 : 0; // Centrar un poco más el recibo sencillo
      
      for (let i = 0; i < numRecibos; i++) {
        const yOffset = tipoRecibo === "doble" 
          ? i * (pageHeight / 2) 
          : baseYOffset;
        
        // Línea divisoria entre recibos (solo para recibo doble)
        if (tipoRecibo === "doble" && i === 1) {
          doc.setLineDash([3, 3]);
          doc.setDrawColor(150, 150, 150);
          doc.line(10, yOffset, pageWidth - 10, yOffset);
          doc.setLineDash([]);
          // Texto de corte
          doc.setFontSize(6);
          doc.setTextColor(150, 150, 150);
          doc.text('✂ Cortar por esta línea', pageWidth / 2, yOffset - 2, { align: 'center' });
        }

        // Logo y Header
        if (logoBase64) {
          try {
            doc.addImage(logoBase64, 'PNG', 15, yOffset + 8, 30, 30);
          } catch (e) {
            console.error('Error adding logo:', e);
          }
        }

        // Header text (al lado del logo)
        doc.setFontSize(20);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(79, 70, 229); // Indigo
        doc.text('RECIBO DE PAGO', 50, yOffset + 20);
        
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text('ROTCO - Administración de Propiedades', 50, yOffset + 27);

        // Número de recibo (derecha)
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(`No. Recibo:`, pageWidth - 60, yOffset + 15);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(79, 70, 229);
        doc.text(`${reciboId}`, pageWidth - 60, yOffset + 21);
        
        doc.setTextColor(0, 0, 0);
        doc.setFont(undefined, 'bold');
        doc.text(`Fecha:`, pageWidth - 60, yOffset + 30);
        doc.setFont(undefined, 'normal');
        doc.text(`${formatDate(fechaPago)}`, pageWidth - 60, yOffset + 36);

        // Línea separadora
        doc.setDrawColor(79, 70, 229);
        doc.setLineWidth(0.5);
        doc.line(15, yOffset + 42, pageWidth - 15, yOffset + 42);
        doc.setLineWidth(0.2);

        // Información del inmueble y contrato (caja gris)
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(15, yOffset + 46, pageWidth - 30, 28, 2, 2, 'F');
        
        doc.setFontSize(8);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(79, 70, 229);
        doc.text('DATOS DEL INMUEBLE', 20, yOffset + 53);
        
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        doc.setFont(undefined, 'bold');
        doc.text('Inmueble:', 20, yOffset + 60);
        doc.setFont(undefined, 'normal');
        doc.text(contrato.inmueble_nombre, 42, yOffset + 60);
        
        doc.setFont(undefined, 'bold');
        doc.text('Dirección:', 20, yOffset + 66);
        doc.setFont(undefined, 'normal');
        // Truncate address if too long
        const direccion = contrato.inmueble_direccion.length > 60 
          ? contrato.inmueble_direccion.substring(0, 57) + '...' 
          : contrato.inmueble_direccion;
        doc.text(direccion, 42, yOffset + 66);
        
        doc.setFont(undefined, 'bold');
        doc.text('Contrato:', 20, yOffset + 72);
        doc.setFont(undefined, 'normal');
        doc.text(contrato.numero_contrato, 42, yOffset + 72);

        // Información del pago
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.text('RECIBÍ DE:', 20, yOffset + 84);
        doc.setFont(undefined, 'normal');
        doc.text(contrato.inquilino_nombre.toUpperCase(), 48, yOffset + 84);

        // Cantidad con recuadro destacado
        doc.setFont(undefined, 'bold');
        doc.text('LA CANTIDAD DE:', 20, yOffset + 92);
        
        // Recuadro para el monto
        doc.setFillColor(79, 70, 229);
        doc.roundedRect(55, yOffset + 87, 50, 8, 1, 1, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(11);
        doc.text(formatCurrency(importePago), 80, yOffset + 93, { align: 'center' });
        
        // Cantidad en letras
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        const partes = importePago.toString().split('.');
        const enteros = parseInt(partes[0]);
        const centavos = partes[1] ? parseInt(partes[1].padEnd(2, '0').substring(0, 2)) : 0;
        const cantidadLetras = `(${numeroALetras(enteros)} PESOS ${centavos.toString().padStart(2, '0')}/100 M.N.)`;
        doc.text(cantidadLetras, 20, yOffset + 100);

        // Concepto
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.text('POR CONCEPTO DE:', 20, yOffset + 108);
        doc.setFont(undefined, 'normal');
        doc.text(cargo.descripcion || cargo.tipo, 60, yOffset + 108);

        // Método de pago
        doc.setFont(undefined, 'bold');
        doc.text('MÉTODO DE PAGO:', 20, yOffset + 116);
        doc.setFont(undefined, 'normal');
        doc.text(metodoPago, 60, yOffset + 116);

        // Referencia si existe
        if (referenciaPago) {
          doc.setFont(undefined, 'bold');
          doc.text('REFERENCIA:', 110, yOffset + 116);
          doc.setFont(undefined, 'normal');
          doc.text(referenciaPago, 140, yOffset + 116);
        }

        // Firma
        doc.setDrawColor(0, 0, 0);
        doc.line(130, yOffset + 130, pageWidth - 20, yOffset + 130);
        doc.setFontSize(7);
        doc.setTextColor(100, 100, 100);
        doc.text('Firma de quien recibe', (130 + pageWidth - 20) / 2, yOffset + 135, { align: 'center' });

        // Footer
        doc.setFontSize(6);
        doc.text('Este recibo certifica el pago recibido en la fecha indicada', pageWidth / 2, yOffset + 142, { align: 'center' });
        doc.text('ROTCO - Sistema de Administración de Propiedades', pageWidth / 2, yOffset + 146, { align: 'center' });
      }

      // Guardar PDF con nombre descriptivo
      const tipoSufijo = tipoRecibo === "doble" ? "Impresion" : "Digital";
      const fileName = `Recibo_${tipoSufijo}_${reciboId}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      const mensaje = tipoRecibo === "doble" 
        ? "Recibo doble generado (para impresión)" 
        : "Recibo sencillo generado (para envío digital)";
      toast.success(mensaje);
    } catch (error) {
      toast.error("Error al generar el recibo");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const contratoSeleccionado = contratos.find(c => c.id === selectedContrato);
  const cargoSeleccionado = cargos.find(c => c.id === selectedCargo);
  const pagoSeleccionado = pagos.find(p => p.id === selectedPago);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-4xl mx-auto space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Generación de Documentos</h1>
            <p className="text-gray-500 mt-1">Crea recibos y otros documentos oficiales</p>
          </div>
          <FileText className="w-12 h-12 text-indigo-600" />
        </div>

        {/* Tipo de Documento */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Nuevo Recibo de Pago
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Selección de Contrato/Inmueble */}
            <div className="space-y-2">
              <Label htmlFor="contrato">1. Selecciona el Inmueble / Contrato *</Label>
              <Select value={selectedContrato} onValueChange={setSelectedContrato}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar inmueble..." />
                </SelectTrigger>
                <SelectContent>
                  {contratos.map((contrato) => (
                    <SelectItem key={contrato.id} value={contrato.id}>
                      {contrato.inmueble_nombre} - {contrato.numero_contrato} ({contrato.inquilino_nombre})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Selección de Cargo */}
            {selectedContrato && (
              <div className="space-y-2">
                <Label htmlFor="cargo">2. Selecciona el Cargo *</Label>
                <Select value={selectedCargo} onValueChange={setSelectedCargo}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar cargo..." />
                  </SelectTrigger>
                  <SelectContent>
                    {cargos.length === 0 ? (
                      <SelectItem value="none" disabled>No hay cargos disponibles</SelectItem>
                    ) : (
                      cargos.map((cargo) => (
                        <SelectItem key={cargo.id} value={cargo.id}>
                          {cargo.tipo} - {formatCurrency(cargo.importe)} - Vence: {formatDate(cargo.fecha_vencimiento)}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Selección de Pago (Opcional) */}
            {selectedCargo && (
              <div className="space-y-2">
                <Label htmlFor="pago">3. Selecciona el Pago (Opcional)</Label>
                <Select value={selectedPago} onValueChange={setSelectedPago}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar pago (opcional)..." />
                  </SelectTrigger>
                  <SelectContent>
                    {pagos.length === 0 ? (
                      <SelectItem value="none" disabled>No hay pagos registrados - se usará el saldo del cargo</SelectItem>
                    ) : (
                      pagos.map((pago) => (
                        <SelectItem key={pago.id} value={pago.id}>
                          {formatDate(pago.fecha_pago)} - {formatCurrency(pago.importe)} - {pago.metodo_pago}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  Si no seleccionas un pago, el recibo se generará con el saldo pendiente del cargo y la fecha actual.
                </p>
              </div>
            )}

            {/* Tipo de Recibo */}
            {selectedCargo && (
              <div className="space-y-3">
                <Label>4. Tipo de Recibo</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setTipoRecibo("doble")}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      tipoRecibo === "doble"
                        ? "border-indigo-500 bg-indigo-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Copy className={`w-6 h-6 ${tipoRecibo === "doble" ? "text-indigo-600" : "text-gray-500"}`} />
                      <span className={`font-medium ${tipoRecibo === "doble" ? "text-indigo-700" : "text-gray-700"}`}>
                        Recibo Doble
                      </span>
                      <span className="text-xs text-gray-500 text-center">
                        Para imprimir y recoger efectivo
                      </span>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setTipoRecibo("sencillo")}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      tipoRecibo === "sencillo"
                        ? "border-indigo-500 bg-indigo-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Send className={`w-6 h-6 ${tipoRecibo === "sencillo" ? "text-indigo-600" : "text-gray-500"}`} />
                      <span className={`font-medium ${tipoRecibo === "sencillo" ? "text-indigo-700" : "text-gray-700"}`}>
                        Recibo Sencillo
                      </span>
                      <span className="text-xs text-gray-500 text-center">
                        Para enviar por WhatsApp/Email
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Vista Previa */}
            {cargoSeleccionado && (
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="font-semibold mb-3">Vista Previa del Recibo:</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">Inmueble:</p>
                    <p className="font-medium">{contratoSeleccionado?.inmueble_nombre}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Contrato:</p>
                    <p className="font-medium">{contratoSeleccionado?.numero_contrato}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Inquilino:</p>
                    <p className="font-medium">{contratoSeleccionado?.inquilino_nombre}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Concepto:</p>
                    <p className="font-medium">{cargoSeleccionado?.descripcion || cargoSeleccionado?.tipo}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Fecha:</p>
                    <p className="font-medium">
                      {pagoSeleccionado 
                        ? formatDate(pagoSeleccionado.fecha_pago) 
                        : formatDate(new Date().toISOString().split('T')[0]) + ' (Hoy)'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Importe:</p>
                    <p className="font-bold text-lg text-green-600">
                      {pagoSeleccionado 
                        ? formatCurrency(pagoSeleccionado.importe) 
                        : formatCurrency(cargoSeleccionado?.saldo || cargoSeleccionado?.importe)}
                    </p>
                  </div>
                </div>
                {!pagoSeleccionado && (
                  <p className="text-xs text-amber-600 mt-3">
                    ⚠️ Sin pago seleccionado - el recibo se generará con los datos del cargo
                  </p>
                )}
              </div>
            )}

            {/* Botones de Acción */}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={generarRecibo}
                disabled={!selectedCargo || loading}
                className="flex-1"
              >
                <Download className="w-4 h-4 mr-2" />
                {loading ? "Generando..." : "Generar Recibo"}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedContrato("");
                  setSelectedCargo("");
                  setSelectedPago("");
                }}
              >
                Limpiar
              </Button>
            </div>

            {/* Información adicional */}
            <div className="text-xs text-gray-500 mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
              <p className="font-semibold mb-1">📋 Formato del recibo:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>PDF tamaño carta (21.5 x 28 cm)</li>
                {tipoRecibo === "doble" ? (
                  <>
                    <li><strong>Formato duplicado</strong> (dos recibos en una hoja)</li>
                    <li>Línea punteada para cortar por la mitad</li>
                    <li>Ideal para cobro en efectivo</li>
                  </>
                ) : (
                  <>
                    <li><strong>Formato sencillo</strong> (un recibo por hoja)</li>
                    <li>Ideal para envío por WhatsApp o Email</li>
                    <li>Perfecto para pagos por transferencia</li>
                  </>
                )}
                <li>Incluye todos los datos del pago y contrato</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
